/* tslint:disable:max-file-line-count */
import { createPortal } from 'react-dom'
import React, { useEffect, useRef, useState, KeyboardEvent } from 'react'
import qrcode from '../../../../assets/images/ai-assistant/ai-wechat-qrcode-v3.png'
import ctaBusinessClarity from '../../../../assets/images/ai-assistant/cta-business-clarity.webp'
import xiaoguo from '../../../../assets/images/ai-assistant/xiaoguo.webp'
import AiAssistantPluginShowcase from './plugin-showcase'
import AiDownloadMenu from './download-menu'
import { AI_DESKTOP_DOWNLOADS } from './constants'
import Style from './story.module.scss'

const imageSources: Record<string, string> = {
    '/images/ai-assistant/ai-wechat-qrcode-v2.png': qrcode,
    '/images/ai-assistant/ai-wechat-qrcode-v3.png': qrcode,
    '/images/ai-assistant/cta-business-clarity.webp': ctaBusinessClarity,
    '/images/ai-assistant/xiaoguo.webp': xiaoguo,
}

const Image = ({ src, priority: _priority, ...props }: any) => <img src={imageSources[src] || src} {...props} />

type EntryCase = 'profile' | 'document'

interface EntryCaseData {
    label: string
    avatar: string
    sourceLabel: string
    sourceText: string
    placeholder: string
    steps: string[]
    title: string
    rows: string[][]
    tip: string
    button: string
}

interface AiAssistantStoryProps {
    variant: 'desktop' | 'mobile'
}

const entryCases: Record<EntryCase, EntryCaseData> = {
    profile: {
        label: '档案录入',
        avatar: '档',
        sourceLabel: '来源 · 新档案资料',
        sourceText: '新增兴旺餐饮二店会员，并录入精品番茄、青椒两种商品资料。',
        placeholder: '会员资料 / 商品表格也能识别',
        steps: ['识别档案内容', '查找已有资料', '生成档案草稿', '等待人工确认'],
        title: '档案录入草稿',
        rows: [
            ['会员录入', '兴旺餐饮二店 · 李经理'],
            ['商品录入', '精品番茄 · 青椒'],
            ['录入结果', '2 类档案待确认'],
        ],
        tip: '核对会员和商品信息后才会写入系统',
        button: '确认录入档案',
    },
    document: {
        label: '单据录入',
        avatar: '单',
        sourceLabel: '来源 · 群消息和采购清单',
        sourceText: '兴旺餐饮明早要货 30 件；陈老板脐橙 0828 批次入库 80 件。',
        placeholder: '聊天图片 / 采购清单也能识别',
        steps: ['识别单据信息', '匹配往来单位商品', '生成单据草稿', '等待人工确认'],
        title: '单据录入草稿',
        rows: [
            ['开票单', '兴旺餐饮二店 · 30 件'],
            ['入库单', '脐橙 0828 · 80 件'],
            ['单据状态', '2 张单据待确认'],
        ],
        tip: '核对开票和入库信息后才会写入系统',
        button: '确认录入单据',
    },
}

const plugins = [
    {
        illustration: 'market',
        name: '果蔬行情分析',
        author: '产地行情观察室 · 4.9 分',
        description: '汇总公开行情与本店进销价，生成趋势判断和进货建议。',
        status: '开发中',
    },
    {
        illustration: 'customer',
        name: '沉默客户唤醒',
        author: '武汉果批老周 · 4.8 分',
        description: '找到近期没来的老客户，自动整理回访名单和沟通话术。',
        status: '开发中',
    },
    {
        illustration: 'inventory',
        name: '滞销库存预警',
        author: '森果云官方 · 4.9 分',
        description: '结合库存天数和销量变化，提前发现批次积压风险。',
        status: '开发中',
    },
    {
        illustration: 'reconcile',
        name: '供应商对账助手',
        author: '长沙联营档口 · 4.7 分',
        description: '按供应商汇总入库、退货和付款记录，快速核对差异。',
        status: '开发中',
    },
] as const

const cx = (...names: Array<string | false | undefined>) =>
    names
        .filter(Boolean)
        .map(name => Style[name as string])
        .join(' ')

export default function AiAssistantStory({ variant }: AiAssistantStoryProps) {
    const [entryCase, setEntryCase] = useState<EntryCase>('profile')
    const [consultOpen, setConsultOpen] = useState(false)
    const entryTabRefs = useRef<Array<HTMLButtonElement | null>>([])
    const consultTriggerRef = useRef<HTMLButtonElement>(null)
    const consultCloseRef = useRef<HTMLButtonElement>(null)
    const tabGroupID = 'ai-assistant-story'
    const entry = entryCases[entryCase]

    useEffect(() => {
        if (!consultOpen) return

        const previousOverflow = document.body.style.overflow
        const trigger = consultTriggerRef.current
        document.body.style.overflow = 'hidden'
        consultCloseRef.current?.focus()

        const handleKeyDown = (event: globalThis.KeyboardEvent) => {
            if (event.key === 'Escape') setConsultOpen(false)
            if (event.key === 'Tab') {
                event.preventDefault()
                consultCloseRef.current?.focus()
            }
        }

        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.body.style.overflow = previousOverflow
            document.removeEventListener('keydown', handleKeyDown)
            trigger?.focus()
        }
    }, [consultOpen])

    const handleEntryTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
        const keys: EntryCase[] = ['profile', 'document']
        let nextIndex: number | null = null
        if (['ArrowRight', 'ArrowDown'].includes(event.key)) nextIndex = (index + 1) % keys.length
        if (['ArrowLeft', 'ArrowUp'].includes(event.key)) nextIndex = (index - 1 + keys.length) % keys.length
        if (event.key === 'Home') nextIndex = 0
        if (event.key === 'End') nextIndex = keys.length - 1
        if (nextIndex === null) return

        event.preventDefault()
        setEntryCase(keys[nextIndex])
        entryTabRefs.current[nextIndex]?.focus()
    }

    return (
        <div className={Style.story}>
            <section className={cx('capability-section', 'white')} id="query">
                <div className={Style['section-grid']}>
                    <SectionCopy
                        index="连接森果云数据查询"
                        title={
                            <>
                                全盘数据连起来
                                <br />
                                <em>一句话问清经营情况</em>
                            </>
                        }
                        description="登录店铺后，小果连接当前账号有权限查看的商品、批次、客户、销售和库存数据，按你的要求做报表、找原因、给建议。"
                        points={[
                            ['✓', '哪些商品、哪些批次利润更高', '把售价、成本和损耗放在一起看，找到真正赚钱的货。'],
                            ['✓', '哪些客户更值得重点维护', '综合贡献、复购、活跃度与欠款，看清长期价值。'],
                            ['✓', '哪些经营环节有风险', '库存积压、毛利异常、客户沉默等变化及时提醒。'],
                        ]}
                    />
                    <div className={Style.visual}>
                        <div className={Style['query-scene']}>
                            <div className={Style['query-question-float']}>
                                “帮我看看这个月什么货更赚钱，哪些客户最值得维护？”
                            </div>
                            <div className={Style['query-report-paper']}>
                                <div className={Style['query-paper-head']}>
                                    <span>本月经营分析 · 数据截至今日 09:30</span>
                                    <em>● 已完成</em>
                                </div>
                                <h3>利润结构变好，库存周转仍需关注</h3>
                                <div className={Style['query-paper-grid']}>
                                    <div>
                                        <span>高利润商品</span>
                                        <strong>精品番茄</strong>
                                        <small>毛利率 31.2%</small>
                                    </div>
                                    <div>
                                        <span>高价值客户</span>
                                        <strong>兴旺餐饮</strong>
                                        <small>本月贡献 +22%</small>
                                    </div>
                                    <div>
                                        <span>高利润批次</span>
                                        <strong>脐橙 0826</strong>
                                        <small>单批毛利 ¥8,620</small>
                                    </div>
                                    <div>
                                        <span>经营机会</span>
                                        <strong>沉默客户召回</strong>
                                        <small>预计可唤醒 6 位</small>
                                    </div>
                                </div>
                                <div className={Style['query-paper-foot']}>
                                    <b>风险提醒</b>3 个批次周转速度下降，建议优先促销或调价。
                                </div>
                            </div>
                            <Image
                                className={Style['xiaoguo-figure']}
                                src="/images/ai-assistant/xiaoguo.webp"
                                alt=""
                                width={225}
                                height={225}
                            />
                            <div className={Style['xiaoguo-caption']}>数据我来串，重点你一眼看懂。</div>
                            <div className={cx('query-clue', 'profit')}>
                                <i />
                                已对比商品与批次利润
                            </div>
                            <div className={cx('query-clue', 'customer')}>
                                <i />
                                已找到高价值客户
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className={cx('capability-section', 'tint')} id="entry">
                <div className={Style['section-grid']}>
                    <SectionCopy
                        index="数据录入"
                        status="开发中"
                        title={
                            <>
                                档案和单据免手输
                                <br />
                                <em className={Style['entry-title-nowrap']}>一句话、一张图全搞定</em>
                            </>
                        }
                        description="发语音，拍照片或截图，小果自动识别生成草稿，您只需一键确认即可录入系统。"
                        points={[
                            ['档', '基础建档', '商品、会员、供货商资料批量录入，免去手动逐个填写。'],
                            ['单', '单据识别', '微信消息截图、手写单等自动识别，秒级生成单据草稿。'],
                            ['人', '核对再入账', '草稿确认无误再同步进系统，账目数据绝对精准可控。'],
                        ]}
                    />
                    <div className={Style.visual}>
                        <div className={Style['entry-stage']}>
                            <div className={cx('entry-switch', 'case-tabs')} role="tablist" aria-label="数据录入示例">
                                {(['profile', 'document'] as EntryCase[]).map((key, index) => {
                                    const active = entryCase === key

                                    return (
                                        <button
                                            key={key}
                                            ref={element => {
                                                entryTabRefs.current[index] = element
                                            }}
                                            id={`${tabGroupID}-${key}-tab`}
                                            type="button"
                                            role="tab"
                                            aria-selected={active}
                                            aria-controls={`${tabGroupID}-panel`}
                                            tabIndex={active ? 0 : -1}
                                            className={active ? Style.active : undefined}
                                            onClick={() => setEntryCase(key)}
                                            onKeyDown={event => handleEntryTabKeyDown(event, index)}
                                        >
                                            {entryCases[key].label}
                                        </button>
                                    )
                                })}
                            </div>
                            <div className={Style['chat-paper']}>
                                <div className={Style['chat-avatar-row']}>
                                    <i>{entry.avatar}</i>
                                    <div className={Style['chat-message']}>
                                        <span>{entry.sourceLabel}</span>
                                        <div>{entry.sourceText}</div>
                                    </div>
                                </div>
                                <div className={Style['chat-image-placeholder']}>{entry.placeholder}</div>
                            </div>
                            <div className={Style['entry-bridge']}>
                                <Image src="/images/ai-assistant/xiaoguo.webp" alt="" width={225} height={225} />
                                <span>已识别并匹配</span>
                            </div>
                            <div
                                id={`${tabGroupID}-panel`}
                                className={Style['entry-document']}
                                role="tabpanel"
                                aria-labelledby={`${tabGroupID}-${entryCase}-tab`}
                                tabIndex={0}
                            >
                                <div className={Style['document-head']}>
                                    <b>{entry.title}</b>
                                    <span>等待确认</span>
                                </div>
                                <div className={Style['document-rows']}>
                                    {entry.rows.map(([label, value]) => (
                                        <div key={label}>
                                            <span>{label}</span>
                                            <b>{value}</b>
                                        </div>
                                    ))}
                                </div>
                                <div className={Style['confirm-row']}>
                                    <small>{entry.tip}</small>
                                    <button type="button">{entry.button}</button>
                                </div>
                            </div>
                            <div className={Style['entry-flow-new']}>
                                {entry.steps.map((step, index) => (
                                    <div key={step}>
                                        <span>第 {index + 1} 步</span>
                                        <b>{step}</b>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className={cx('capability-section', 'white')} id="custom">
                <div className={Style['section-grid']}>
                    <SectionCopy
                        index="插件功能"
                        title={
                            <>
                                想要自定义功能
                                <br />
                                <em>直接让小果做</em>
                            </>
                        }
                        description="过去要提需求、排计划、等开发；现在把想法说清楚，小果能快速生成新页面和新功能。"
                        points={[
                            ['说', '一句话描述想法', '不用画原型，也不用懂技术，说清楚经营上想解决什么。'],
                            ['看', '立刻看到可用页面', '新看板、新入口和新流程先生成效果，边看边调。'],
                            ['改', '继续用人话调整', '哪里不顺手就接着说，字号、字段和规则都能继续改。'],
                        ]}
                    />
                    <div className={Style.visual}>
                        <AiAssistantPluginShowcase demoOnly prompt="帮我在左侧加一个经营看板。" />
                    </div>
                </div>
            </section>

            <section className={Style['market-section-new']} id="market">
                <div className={Style['market-layout']}>
                    <div className={Style['market-intro-new']}>
                        <p className={Style['section-index']}>插件生态</p>
                        <h2>
                            同行好用的插件
                            <br />
                            <em>你可一键安装</em>
                        </h2>
                        <p>
                            插件市场里既有森果云提供的能力，也有大家上传分享的经营插件。别人已经跑通的好功能，可以直接安装，再按自己的想法调整。
                        </p>
                        <div className={Style['market-sign']}>
                            <Image src="/images/ai-assistant/xiaoguo.webp" alt="" width={225} height={225} />
                            <div>
                                <b>
                                    小果插件市场 <em>开发中</em>
                                </b>
                                <span>同行正在使用的经营好工具</span>
                            </div>
                        </div>
                    </div>
                    <div className={Style['market-stage-new']}>
                        <div className={Style['market-shelf']}>
                            {plugins.map(plugin => (
                                <article className={Style['market-plugin']} key={plugin.name}>
                                    <div className={Style['market-plugin-top']}>
                                        <b>{plugin.name}</b>
                                        <small>{plugin.author}</small>
                                    </div>
                                    <p>{plugin.description}</p>
                                    <div className={Style['plugin-meta']}>
                                        <span>{plugin.status}</span>
                                    </div>
                                    <PluginIllustration type={plugin.illustration} />
                                </article>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className={Style['general-section-new']} id="general">
                <div className={Style['general-layout']}>
                    <div className={Style['general-intro-new']}>
                        <p className={Style['section-index']}>通用工作能力</p>
                        <h2>
                            这些工作
                            <br />
                            <em>小果也能做</em>
                        </h2>
                        <p>写文案、做图片、拟/审合同、做营销方案和报表，还能联通钉钉、企业微信和飞书。</p>
                    </div>
                    <div className={Style['ability-map']} aria-label="小果通用 AI 能力图">
                        <svg
                            className={Style['ability-lines']}
                            viewBox="0 0 700 400"
                            preserveAspectRatio="none"
                            aria-hidden="true"
                        >
                            <defs>
                                <linearGradient id={`${tabGroupID}-ability-line-gradient`} x1="0" y1="0" x2="1" y2="1">
                                    <stop offset="0" stopColor="#0aadf5" />
                                    <stop offset="1" stopColor="#8656fa" />
                                </linearGradient>
                            </defs>
                            <g style={{ stroke: `url(#${tabGroupID}-ability-line-gradient)` }}>
                                <path d="M350 200 C290 140 220 88 92 36" />
                                <path d="M350 200 C410 140 480 88 608 36" />
                                <path d="M350 200 C270 200 190 200 76 200" />
                                <path d="M350 200 C430 200 510 200 624 200" />
                                <path d="M350 200 C290 264 215 316 110 364" />
                                <path d="M350 200 C410 264 485 316 590 364" />
                            </g>
                            <circle cx="92" cy="36" r="5" />
                            <circle cx="608" cy="36" r="5" />
                            <circle cx="76" cy="200" r="5" />
                            <circle cx="624" cy="200" r="5" />
                            <circle cx="110" cy="364" r="5" />
                            <circle cx="590" cy="364" r="5" />
                        </svg>
                        <div className={Style['ability-orb']}>
                            <div className={Style['ability-orb-core']}>
                                <Image
                                    src="/images/ai-assistant/xiaoguo.webp"
                                    alt="小果 AI 助手"
                                    width={225}
                                    height={225}
                                />
                            </div>
                            <div className={Style['ability-orb-label']}>
                                <i />
                                小果能力中心
                            </div>
                        </div>
                        <AbilityNode
                            className="marketing"
                            icon="营"
                            title="做营销方案"
                            description="从活动主题到客户触达话术"
                        />
                        <AbilityNode
                            className="image"
                            icon="图"
                            title="做图片"
                            description="商品宣传图、活动海报和配图"
                        />
                        <AbilityNode
                            className="contract"
                            icon="合"
                            title="拟 / 审合同"
                            description="起草、审阅合作条款，生成修改建议"
                        />
                        <AbilityNode className="systems" icon="联" title="联通办公系统">
                            <div className={Style['system-badges']}>
                                <span>钉钉</span>
                                <span>企业微信</span>
                                <span>飞书</span>
                            </div>
                        </AbilityNode>
                        <AbilityNode
                            className="analysis"
                            icon="析"
                            title="数据分析"
                            description="找变化、看原因，给出经营建议"
                        />
                        <AbilityNode
                            className="report"
                            icon="表"
                            title="做报表"
                            description="整理数据，生成图表、结论和摘要"
                        />
                    </div>
                </div>
            </section>

            <section className={cx('capability-section', 'tint', 'permission-section')} id="permission">
                <div className={Style['section-grid']}>
                    <SectionCopy
                        index="数据权限"
                        title={
                            <>
                                AI 可以灵活
                                <br />
                                <em>数据边界不能含糊</em>
                            </>
                        }
                        description="小果不是把整店数据打包交出去，而是按当前账号权限和本次任务取用必要信息；遇到写入操作，还要经过人的确认。"
                        points={[
                            ['店', '业务记录仍在森果云', '订单、库存、客户等业务记录继续由森果云系统保存和管理。'],
                            ['权', '只读取当前账号有权限的数据', '店铺、岗位和账号原有的权限边界继续生效。'],
                            ['确', '写入操作需要人确认', '开票、入库等操作先生成草稿，确认后才正式执行。'],
                            [
                                '本',
                                '软件本地运行，数据留在本地',
                                'AI 助手在您的电脑上运行，处理过程中的数据留在本地，不会把整店数据打包上传。',
                            ],
                        ]}
                    />
                    <div className={Style.visual}>
                        <div className={Style['permission-stage-new']}>
                            <article className={cx('permission-ticket', 'top')}>
                                <span>只读取当前账号能看的</span>
                                <h3>查看近 7 天销售趋势</h3>
                                <p>使用当前店铺的销售汇总数据，不会查询其他店铺。</p>
                                <footer>
                                    <span>武汉白沙洲店 · 管理员</span>
                                    <b>允许查看</b>
                                </footer>
                            </article>
                            <article className={cx('permission-ticket', 'bottom')}>
                                <span>写入需要人工确认</span>
                                <h3>生成销售开票单草稿</h3>
                                <p>小果先整理客户、商品和数量，确认无误后才正式写入系统。</p>
                                <footer>
                                    <span>草稿已生成 · 尚未写入</span>
                                    <b>等待确认</b>
                                </footer>
                            </article>
                        </div>
                    </div>
                </div>
            </section>

            <section className={Style['consult-section']} id="consult">
                <div className={Style['consult-shell']}>
                    <div className={Style['consult-copy']}>
                        <span className={Style['consult-eyebrow']}>AI 实施服务</span>
                        <h2>
                            不知道 AI 怎么用
                            <br />
                            <em>我们来帮你</em>
                        </h2>
                        <p>
                            先从每天最费时间的一件事开始。小果先做出能用的一版，陪你放进真实工作里试，再根据反馈一点点调成你的工作方式。
                        </p>
                        <button
                            ref={consultTriggerRef}
                            className={cx('primary-button', 'consult-trigger')}
                            type="button"
                            aria-haspopup="dialog"
                            aria-controls={`${tabGroupID}-consult-modal`}
                            onClick={() => setConsultOpen(true)}
                        >
                            免费咨询 <span aria-hidden="true">↗</span>
                        </button>
                    </div>
                    <div
                        className={Style['consult-scene']}
                        aria-label="小果陪你从真实问题开始，边用边调整，跑出自己的 AI 工作流程"
                    >
                        <div className={Style['consult-question']}>
                            <small>不用先想清楚怎么用 AI</small>
                            <b>先告诉小果，你每天最头疼的是哪件事。</b>
                        </div>
                        <div className={Style['consult-avatar']}>
                            <Image
                                src="/images/ai-assistant/xiaoguo.webp"
                                alt="小果 AI 助手"
                                width={225}
                                height={225}
                            />
                        </div>
                        <div className={Style['consult-companion']}>
                            <i />
                            小果陪你边做边调
                        </div>
                        <ConsultStep
                            className="one"
                            number="01"
                            title="先做出能用的一版"
                            description="小果理解问题，搭好第一版流程"
                        />
                        <ConsultStep
                            className="two"
                            number="02"
                            title="放进真实工作里试"
                            description="员工照常做事，小果在旁协助"
                        />
                        <ConsultStep
                            className="three"
                            number="03"
                            title="边用边调成自己的"
                            description="哪里不顺就继续说，越用越顺手"
                        />
                    </div>
                </div>
            </section>

            <section className={Style.cta} id="download">
                <div className={Style['cta-box']}>
                    <div>
                        <h2>用 AI 重塑经营效率，从今天开始</h2>
                        <p>查数据、算利润、做分析，随时随地帮你解决繁琐问题。</p>
                    </div>
                    <Image
                        className={Style['cta-art']}
                        src="/images/ai-assistant/cta-business-clarity.webp"
                        alt=""
                        width={1152}
                        height={768}
                        sizes="340px"
                    />
                    {variant === 'desktop' ? (
                        <AiDownloadMenu
                            downloads={AI_DESKTOP_DOWNLOADS}
                            placement="above"
                            className={Style['cta-download']}
                            triggerClassName={Style['cta-button']}
                        >
                            <strong>下载森果云 AI 助手</strong> <span aria-hidden="true">↓</span>
                        </AiDownloadMenu>
                    ) : (
                        <button type="button" className={cx('cta-button', 'computer-only-button')} disabled>
                            <ComputerIcon />
                            仅支持电脑下载
                        </button>
                    )}
                </div>
            </section>

            {consultOpen &&
                createPortal(
                    <div className={Style['consult-modal']} id={`${tabGroupID}-consult-modal`}>
                        <button
                            className={Style['consult-modal-backdrop']}
                            type="button"
                            aria-label="关闭咨询弹窗"
                            onClick={() => setConsultOpen(false)}
                        />
                        <div
                            className={Style['consult-dialog']}
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby={`${tabGroupID}-consult-title`}
                            aria-describedby={`${tabGroupID}-consult-desc`}
                        >
                            <button
                                ref={consultCloseRef}
                                className={Style['consult-close']}
                                type="button"
                                aria-label="关闭"
                                onClick={() => setConsultOpen(false)}
                            >
                                ×
                            </button>
                            <h2 id={`${tabGroupID}-consult-title`}>现在咨询</h2>
                            <p id={`${tabGroupID}-consult-desc`}>微信扫码，告诉我们你最想用 AI 解决什么问题。</p>
                            <div className={Style['consult-qrcode-wrap']}>
                                <Image
                                    src="/images/ai-assistant/ai-wechat-qrcode-v3.png"
                                    alt="森果小英·农企AI实战微信咨询二维码"
                                    width={580}
                                    height={580}
                                    sizes="276px"
                                />
                            </div>
                            {variant === 'mobile' && (
                                <small>
                                    微信名：森果小英·农企AI实战
                                    <br />
                                    长按或扫描二维码添加咨询
                                </small>
                            )}
                        </div>
                    </div>,
                    document.body,
                )}
        </div>
    )
}

function SectionCopy({
    index,
    status,
    title,
    description,
    points,
}: {
    index: string
    status?: string
    title: React.ReactNode
    description: string
    points: ReadonlyArray<readonly [string, string, string]>
}) {
    return (
        <div className={Style['section-copy']}>
            <p className={Style['section-index']}>
                {index}
                {status && <span className={Style['section-status']}>{status}</span>}
            </p>
            <h2>{title}</h2>
            <p className={Style['section-desc']}>{description}</p>
            <div className={Style['point-list']}>
                {points.map(([icon, titleText, descriptionText]) => (
                    <div className={Style.point} key={titleText}>
                        <i className={Style['point-icon']}>{icon}</i>
                        <div>
                            <b>{titleText}</b>
                            <p>{descriptionText}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

function AbilityNode({
    className,
    icon,
    title,
    description,
    children,
}: {
    className: string
    icon: string
    title: string
    description?: string
    children?: React.ReactNode
}) {
    return (
        <article className={cx('ability-node', className)}>
            <i className={Style['ability-node-icon']}>{icon}</i>
            <div>
                <h3>{title}</h3>
                {description && <p>{description}</p>}
                {children}
            </div>
        </article>
    )
}

function ConsultStep({
    className,
    number,
    title,
    description,
}: {
    className: string
    number: string
    title: string
    description: string
}) {
    return (
        <div className={cx('consult-step', className)}>
            <i>{number}</i>
            <div>
                <b>{title}</b>
                <small>{description}</small>
            </div>
        </div>
    )
}

function PluginIllustration({ type }: { type: 'market' | 'customer' | 'inventory' | 'reconcile' }) {
    return (
        <div className={cx('plugin-illustration', `plugin-illustration--${type}`)} aria-hidden="true">
            {type === 'market' && (
                <svg viewBox="0 0 160 100" focusable="false">
                    <rect className={Style['illustration-panel']} x="4" y="8" width="152" height="84" rx="12" />
                    <rect className={Style['illustration-accent']} x="17" y="19" width="34" height="5" rx="2.5" />
                    <rect className={Style['illustration-line']} x="112" y="20" width="28" height="4" rx="2" />
                    <rect className={Style['illustration-soft']} x="17" y="31" width="36" height="18" rx="5" />
                    <rect className={Style['illustration-soft']} x="58" y="31" width="36" height="18" rx="5" />
                    <rect className={Style['illustration-soft']} x="99" y="31" width="36" height="18" rx="5" />
                    <path className={Style['illustration-muted-stroke']} d="M18 78h68M21 74l15-10 13 4 15-12 19 5" />
                    <circle className={Style['illustration-accent']} cx="64" cy="56" r="3.5" />
                    <rect className={Style['illustration-line']} x="99" y="70" width="8" height="8" />
                    <rect className={Style['illustration-line']} x="112" y="63" width="8" height="15" />
                    <rect className={Style['illustration-line']} x="125" y="57" width="8" height="21" />
                    <rect className={Style['illustration-accent']} x="138" y="49" width="8" height="29" />
                </svg>
            )}
            {type === 'customer' && (
                <svg viewBox="0 0 160 100" focusable="false">
                    <rect className={Style['illustration-panel']} x="4" y="8" width="152" height="84" rx="12" />
                    <rect className={Style['illustration-soft']} x="14" y="25" width="40" height="50" rx="10" />
                    <circle className={Style['illustration-accent-stroke']} cx="34" cy="42" r="8" />
                    <path className={Style['illustration-accent-stroke']} d="M20 68c2-11 7-16 14-16s12 5 14 16" />
                    <path className={Style['illustration-dash']} d="M57 50h14m-5-5 5 5-5 5" />
                    <rect className={Style['illustration-panel']} x="76" y="21" width="45" height="58" rx="10" />
                    <path
                        className={Style['illustration-accent-stroke']}
                        d="M91 52h15m-13-1c0-7 2-11 6-11s7 4 7 11l4 5H89Zm4 6c1 4 5 4 7 0"
                    />
                    <path className={Style['illustration-dash']} d="M124 50h10m-5-5 5 5-5 5" />
                    <circle className={Style['illustration-panel']} cx="143" cy="50" r="10" />
                    <path className={Style['illustration-accent-stroke']} d="m138 50 4 4 7-9" />
                </svg>
            )}
            {type === 'inventory' && (
                <svg viewBox="0 0 160 100" focusable="false">
                    <rect className={Style['illustration-panel']} x="4" y="8" width="152" height="84" rx="12" />
                    <rect className={Style['illustration-soft']} x="17" y="20" width="37" height="18" rx="5" />
                    <rect className={Style['illustration-soft']} x="61" y="20" width="37" height="18" rx="5" />
                    <rect className={Style['illustration-soft']} x="105" y="20" width="37" height="18" rx="5" />
                    <path className={Style['illustration-muted-stroke']} d="M17 78h125" />
                    <rect className={Style['illustration-line']} x="24" y="62" width="14" height="16" rx="2" />
                    <rect className={Style['illustration-line']} x="46" y="54" width="14" height="24" rx="2" />
                    <rect className={Style['illustration-line']} x="68" y="47" width="14" height="31" rx="2" />
                    <rect className={Style['illustration-accent']} x="90" y="39" width="14" height="39" rx="2" />
                    <circle className={Style['illustration-panel']} cx="126" cy="58" r="17" />
                    <path className={Style['illustration-accent-stroke']} d="M126 47v12l8 5" />
                    <circle className={Style['illustration-accent']} cx="126" cy="58" r="2.5" />
                </svg>
            )}
            {type === 'reconcile' && (
                <svg viewBox="0 0 160 100" focusable="false">
                    <rect className={Style['illustration-panel']} x="4" y="8" width="152" height="84" rx="12" />
                    <rect className={Style['illustration-soft']} x="14" y="20" width="52" height="60" rx="8" />
                    <rect className={Style['illustration-soft']} x="94" y="20" width="52" height="60" rx="8" />
                    <rect className={Style['illustration-line']} x="23" y="31" width="27" height="4" rx="2" />
                    <rect className={Style['illustration-line']} x="23" y="43" width="34" height="3" rx="1.5" />
                    <rect className={Style['illustration-accent']} x="23" y="55" width="25" height="4" rx="2" />
                    <rect className={Style['illustration-line']} x="103" y="31" width="27" height="4" rx="2" />
                    <rect className={Style['illustration-line']} x="103" y="43" width="34" height="3" rx="1.5" />
                    <rect className={Style['illustration-accent']} x="103" y="55" width="25" height="4" rx="2" />
                    <path className={Style['illustration-dash']} d="M69 39h20m-6-5 6 5-6 5M91 60H71m6-5-6 5 6 5" />
                    <circle className={Style['illustration-panel']} cx="80" cy="76" r="10" />
                    <path className={Style['illustration-accent-stroke']} d="m75 76 4 4 7-9" />
                </svg>
            )}
        </div>
    )
}

function ComputerIcon() {
    return (
        <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
            <rect x="3" y="4" width="18" height="13" rx="2" />
            <path d="M1.75 20h20.5" />
        </svg>
    )
}
